import DocViewer, { DocRenderer, IConfig } from '@cyntler/react-doc-viewer';
import { DocViewerProps } from '@cyntler/react-doc-viewer/dist/DocViewer';
import { Dialog, DialogProps } from '@material-ui/core';
import { Spinner } from 'copilot-design-system';
import React from 'react';

import { AudioRenderer } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/AudioRenderer';
import { ClickAwayContext } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/context';
import { CsvRenderer } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/CsvRenderer';
import { FilePreviewHeader } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/FilePreviewHeader';
import { ImageRenderer } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/ImageRenderer';
import { NoPreviewAvailable } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/NoPreviewAvailable';
import { OctetStreamRenderer } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/OctetSteamRenderer';
import { PdfRenderer } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/PdfRenderer';
import TXTRenderer from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/TxtRenderer';
import { VideoRenderer } from 'src/legacy/components/Files/ChannelContent/components/filePreviewDialog/VideoRenderer';

const PluginRenderers: DocRenderer[] = [
  PdfRenderer,
  VideoRenderer,
  ImageRenderer,
  CsvRenderer,
  AudioRenderer,
  TXTRenderer,
  OctetStreamRenderer,
];

function useConfig({
  onClose,
  handleDownload,
  handleNoPreview,
  fileHasNoPreview,
  handleGoToFilesTab,
}: {
  onClose: DialogProps['onClose'];
  handleDownload: () => void;
  handleNoPreview: () => void;
  fileHasNoPreview: boolean;
  handleGoToFilesTab?: () => void;
}): React.MutableRefObject<IConfig | undefined> {
  // Config for DocViewer. Defines custom renderers for different file types.
  // Defined as a ref to prevent unnecessary re-creation on each render,
  // avoiding redundant re-renders of the DocViewer component.
  return React.useRef<DocViewerProps['config']>({
    noRenderer: {
      overrideComponent: (props) => (
        <NoPreviewAvailable
          fileType={props.document?.fileType ?? ''}
          onDownloadClicked={handleDownload}
          onNoPreview={handleNoPreview}
        />
      ),
    },
    header: {
      overrideComponent: (props) => (
        <FilePreviewHeader
          onClose={() => onClose?.({}, 'backdropClick')}
          onDownload={!fileHasNoPreview ? handleDownload : undefined}
          handleGoToFilesTab={handleGoToFilesTab}
          {...props}
        />
      ),
    },
    loadingRenderer: {
      overrideComponent: () => (
        <div className="w-full h-screen flex justify-center items-center text-white">
          <Spinner size={10} />
        </div>
      ),
    },
  });
}

export const FilePreviewDialog: React.FC<
  DialogProps & {
    documents: DocViewerProps['documents'];
    handleGoToFilesTab?: () => void;
  }
> = React.memo(({ documents, onClose, handleGoToFilesTab, ...dialogProps }) => {
  const [fileHasNoPreview, setFileHasNoPreview] = React.useState(false);
  const handleNoPreview = () => {
    setFileHasNoPreview(true);
  };
  const handleDownload = () => {
    const currentDocument = documents.at(0);
    if (currentDocument) {
      window.open(currentDocument.uri, '_blank', 'noopener');
    }
  };

  const clickAwayContextValue = React.useMemo(
    () => ({
      onClickAway: () => {
        onClose?.({}, 'backdropClick');
      },
    }),
    [onClose],
  );

  const config = useConfig({
    onClose,
    handleDownload,
    handleNoPreview,
    fileHasNoPreview,
    handleGoToFilesTab,
  });

  return (
    <Dialog
      {...dialogProps}
      fullScreen
      BackdropProps={{
        className: 'bg-black/80',
      }}
      classes={{
        paper: 'bg-transparent scrollbar-light',
      }}
      onClose={onClose}
    >
      <ClickAwayContext.Provider value={clickAwayContextValue}>
        <DocViewer
          style={{
            backgroundColor: 'transparent',
            height: 'fit-content',
            width: '100%',
          }}
          documents={documents}
          prefetchMethod="GET"
          pluginRenderers={PluginRenderers}
          config={config.current}
        />
      </ClickAwayContext.Provider>
    </Dialog>
  );
});
